// frontend/src/hooks/useStaticDocuments.ts - NEW STATIC DOCUMENT-SPECIFIC HOOKS
// Entity-specific hooks for static documents separated from core useApi.ts
// Insert this as a new file: useStaticDocuments.ts

import apiClient from '@/api';
import { 
  useApi, 
  useMutation, 
  validateObjectId, 
  validateObjectIds, 
  normalizeSingleResponse, 
  normalizePaginatedResponse,
  type UseApiOptions,
  type UseMutationOptions
} from './useApi';

// Import only types from API - not functions
import type {
  PaginatedResponse,
  StaticDocument,
  StaticDocumentQueryParams,
  StaticDocumentUpdateRequest,
} from '@/api/types';

// ===== LOCAL INTERFACES =====

/**
 * Document analytics interface
 */
interface DocumentAnalytics {
  totalDocuments: number;
  publicDocuments: number;
  byCategory: Record<string, number>;
  totalSize: number;
  viewCount: number;
  downloadCount: number;
  categories: string[];
  tags: string[];
}

// ===== STATIC DOCUMENT QUERY HOOKS =====

/**
 * Fetch paginated list of static documents with comprehensive filtering
 */
export const useStaticDocuments = (params?: StaticDocumentQueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<StaticDocument>> => {
      
      const response = await apiClient.staticDocuments.list(params);
      
      return normalizePaginatedResponse<StaticDocument>(response);
    },
    {
      immediate: true,
      dependencies: params ? [JSON.stringify(params)] : [],
      retryCount: 2,
      retryDelay: 1000,
      rateLimitKey: 'static_documents_list',
      ...options
    }
  );
};

/**
 * Fetch single static document by ID with comprehensive details
 */
export const useStaticDocument = (id?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<StaticDocument> => {
      validateObjectId(id, 'Static Document ID');
      
      
      const response = await apiClient.staticDocuments.get(id!);
      
      return normalizeSingleResponse<StaticDocument>(response);
    },
    {
      immediate: !!id,
      rateLimitKey: `static_document_${id}`,
      ...options
    }
  );
};

/**
 * Search static documents with advanced filtering
 */
export const useSearchStaticDocuments = (query?: string, filters?: any, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<StaticDocument>> => {
      if (!query?.trim()) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      
      const response = await apiClient.staticDocuments.search(query, filters);
      
      return normalizePaginatedResponse<StaticDocument>(response);
    },
    {
      immediate: !!query?.trim(),
      dependencies: [query, JSON.stringify(filters || {})],
      debounce: 500,
      rateLimitKey: 'static_documents_search',
      ...options
    }
  );
};

// ===== DOCUMENT CATEGORIES AND TAGS HOOKS =====

/**
 * Get available document categories
 */
export const useDocumentCategories = (options?: UseApiOptions) => {
  return useApi(
    async (): Promise<string[]> => {
      
      const response = await apiClient.staticDocuments.getCategories();
      
      return normalizeSingleResponse<string[]>(response);
    },
    {
      immediate: true,
      rateLimitKey: 'document_categories',
      ...options
    }
  );
};

/**
 * Get available document tags
 */
export const useDocumentTags = (options?: UseApiOptions) => {
  return useApi(
    async (): Promise<string[]> => {
      
      const response = await apiClient.staticDocuments.getTags();
      
      return normalizeSingleResponse<string[]>(response);
    },
    {
      immediate: true,
      rateLimitKey: 'document_tags',
      ...options
    }
  );
};

// ===== DOCUMENT VERSION HOOKS =====

/**
 * Get document versions
 */
export const useDocumentVersions = (documentId?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<DocumentVersion[]> => {
      validateObjectId(documentId, 'Document ID');
      
      
      const response = await apiClient.staticDocuments.getVersions(documentId!);
      
      return normalizeSingleResponse<DocumentVersion[]>(response);
    },
    {
      immediate: !!documentId,
      rateLimitKey: `document_versions_${documentId}`,
      ...options
    }
  );
};

/**
 * Get specific document version
 */
export const useDocumentVersion = (documentId?: string, version?: number, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<StaticDocument> => {
      validateObjectId(documentId, 'Document ID');
      
      if (!version || version < 1) {
        throw new Error('Valid version number is required');
      }
      
      
      const response = await apiClient.staticDocuments.getVersion(documentId!, version);
      
      return normalizeSingleResponse<StaticDocument>(response);
    },
    {
      immediate: !!(documentId && version),
      dependencies: [documentId, version],
      rateLimitKey: `document_version_${documentId}_${version}`,
      ...options
    }
  );
};

// ===== STATIC DOCUMENT MUTATION HOOKS =====

/**
 * Upload static documents (files → S3 → create records)
 */
export const useCreateStaticDocument = (options?: UseMutationOptions<any, { files: File[]; metadata?: { description?: string; tags?: string[]; isPublic?: boolean; category?: string; projectId?: string; visibilityGroups?: string[] } }>) => {
  return useMutation(
    async ({ files, metadata }): Promise<any> => {
      if (!files || files.length === 0) {
        throw new Error('At least one file is required');
      }

      const response = await apiClient.staticDocuments.upload(files, metadata);
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Update static document metadata
 */
export const useUpdateStaticDocument = (options?: UseMutationOptions<StaticDocument, { id: string; data: StaticDocumentUpdateRequest }>) => {
  return useMutation(
    async ({ id, data }): Promise<StaticDocument> => {
      validateObjectId(id, 'Static Document ID');
      const response = await apiClient.staticDocuments.update(id, data);
      return normalizeSingleResponse<StaticDocument>(response);
    },
    options
  );
};

/**
 * Delete static document
 */
export const useDeleteStaticDocument = (options?: UseMutationOptions<void, string>) => {
  return useMutation(
    async (id: string): Promise<void> => {
      validateObjectId(id, 'Static Document ID');
      
      
      const response = await apiClient.staticDocuments.delete(id);
      
      return normalizeSingleResponse<void>(response);
    },
    options
  );
};

// ===== DOCUMENT LIFECYCLE HOOKS =====

/**
 * Publish static document
 */
export const usePublishStaticDocument = (options?: UseMutationOptions<StaticDocument, string>) => {
  return useMutation(
    async (id: string): Promise<StaticDocument> => {
      validateObjectId(id, 'Static Document ID');
      
      
      const response = await apiClient.staticDocuments.publish(id);
      
      return normalizeSingleResponse<StaticDocument>(response);
    },
    options
  );
};

/**
 * Unpublish static document
 */
export const useUnpublishStaticDocument = (options?: UseMutationOptions<StaticDocument, string>) => {
  return useMutation(
    async (id: string): Promise<StaticDocument> => {
      validateObjectId(id, 'Static Document ID');
      
      
      const response = await apiClient.staticDocuments.unpublish(id);
      
      return normalizeSingleResponse<StaticDocument>(response);
    },
    options
  );
};

/**
 * Archive static document
 */
export const useArchiveStaticDocument = (options?: UseMutationOptions<StaticDocument, string>) => {
  return useMutation(
    async (id: string): Promise<StaticDocument> => {
      validateObjectId(id, 'Static Document ID');
      
      
      const response = await apiClient.staticDocuments.archive(id);
      
      return normalizeSingleResponse<StaticDocument>(response);
    },
    options
  );
};

/**
 * Restore static document from archive
 */
export const useRestoreStaticDocument = (options?: UseMutationOptions<StaticDocument, string>) => {
  return useMutation(
    async (id: string): Promise<StaticDocument> => {
      validateObjectId(id, 'Static Document ID');
      
      
      const response = await apiClient.staticDocuments.restore(id);
      
      return normalizeSingleResponse<StaticDocument>(response);
    },
    options
  );
};

// ===== DOCUMENT VERSION MANAGEMENT HOOKS =====

/**
 * Revert document to previous version
 */
export const useRevertDocumentToVersion = (options?: UseMutationOptions<StaticDocument, { id: string; version: number }>) => {
  return useMutation(
    async ({ id, version }): Promise<StaticDocument> => {
      validateObjectId(id, 'Static Document ID');
      
      if (!version || version < 1) {
        throw new Error('Valid version number is required');
      }
      
      
      const response = await apiClient.staticDocuments.revertToVersion(id, version);
      
      return normalizeSingleResponse<StaticDocument>(response);
    },
    options
  );
};

// ===== DOCUMENT ANALYTICS HOOKS =====

/**
 * Get document analytics
 */
export const useDocumentAnalytics = (params?: any, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<DocumentAnalytics> => {
      
      // Check if analytics endpoint exists, otherwise use list with analytics
      const response = (apiClient.staticDocuments as any).getAnalytics
        ? await (apiClient.staticDocuments as any).getAnalytics(params)
        : await apiClient.staticDocuments.list({ ...params, analytics: true });
      
      
      // Handle both dedicated analytics response and list response with analytics
      if (response.analytics) {
        return response.analytics;
      }
      
      return normalizeSingleResponse<DocumentAnalytics>(response);
    },
    {
      immediate: true,
      dependencies: params ? [JSON.stringify(params)] : [],
      rateLimitKey: 'document_analytics',
      ...options
    }
  );
};

// ===== BATCH OPERATION HOOKS =====

/**
 * Batch delete static documents
 */
export const useBatchDeleteStaticDocuments = (options?: UseMutationOptions<any, string[]>) => {
  return useMutation(
    async (ids: string[]): Promise<any> => {
      if (!ids || !Array.isArray(ids) || ids.length === 0) {
        throw new Error('At least one document ID is required');
      }
      
      // Validate all document IDs
      ids.forEach((id, index) => {
        validateObjectId(id, `Document ${index + 1} ID`);
      });
      
      
      // Use batch operation if available, otherwise delete individually
      if ((apiClient.staticDocuments as any).batchDelete) {
        const response = await (apiClient.staticDocuments as any).batchDelete(ids);
        return normalizeSingleResponse(response);
      } else {
        // Fallback to individual deletes
        const results = await Promise.allSettled(
          ids.map(id => apiClient.staticDocuments.delete(id))
        );
        
        const successful = results.filter(r => r.status === 'fulfilled').length;
        const failed = results.filter(r => r.status === 'rejected').length;
        
        
        return {
          success: true,
          deleted: successful,
          failed: failed,
          message: `Successfully deleted ${successful} documents${failed > 0 ? `, failed to delete ${failed}` : ''}`
        };
      }
    },
    options
  );
};

/**
 * Batch update static documents
 */
export const useBatchUpdateStaticDocuments = (options?: UseMutationOptions<any, { 
  ids: string[]; 
  updates: Partial<StaticDocumentUpdateRequest> 
}>) => {
  return useMutation(
    async ({ ids, updates }): Promise<any> => {
      if (!ids || !Array.isArray(ids) || ids.length === 0) {
        throw new Error('At least one document ID is required');
      }
      
      if (!updates || typeof updates !== 'object') {
        throw new Error('Updates object is required');
      }
      
      // Validate all document IDs
      ids.forEach((id, index) => {
        validateObjectId(id, `Document ${index + 1} ID`);
      });
      
      // Validate visibility groups if provided in updates
      if (updates.visibilityGroups && Array.isArray(updates.visibilityGroups)) {
        updates.visibilityGroups.forEach((groupId: string, index: number) => {
          validateObjectId(groupId, `Visibility Group ${index + 1} ID`);
        });
      }
      
      // Validate tags if provided in updates
      if (updates.tags && Array.isArray(updates.tags)) {
        updates.tags.forEach((tag: string, index: number) => {
          if (typeof tag !== 'string' || tag.trim().length === 0) {
            throw new Error(`Tag ${index + 1} must be a non-empty string`);
          }
          if (tag.length > 50) {
            throw new Error(`Tag ${index + 1} cannot exceed 50 characters`);
          }
        });
      }
      
      
      // Use batch operation if available, otherwise update individually
      if ((apiClient.staticDocuments as any).batchUpdate) {
        const response = await (apiClient.staticDocuments as any).batchUpdate(ids, updates);
        return normalizeSingleResponse(response);
      } else {
        // Fallback to individual updates
        const results = await Promise.allSettled(
          ids.map(id => apiClient.staticDocuments.update(id, updates))
        );
        
        const successful = results.filter(r => r.status === 'fulfilled').length;
        const failed = results.filter(r => r.status === 'rejected').length;
        
        
        return {
          success: true,
          updated: successful,
          failed: failed,
          message: `Successfully updated ${successful} documents${failed > 0 ? `, failed to update ${failed}` : ''}`
        };
      }
    },
    options
  );
};

